import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'

interface DataPoint {
  label: string
  value: number
  color?: string
}

interface DonutChartProps {
  data: DataPoint[]
  width?: number
  height?: number
  innerRadius?: number
  title?: string
  showLabels?: boolean
  showPercentages?: boolean
  showLegend?: boolean
  colors?: string[]
  animate?: boolean
  centerText?: string
  onSliceClick?: (data: DataPoint) => void
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  width = 400,
  height = 400,
  innerRadius = 0.6,
  title,
  showLabels = true,
  showPercentages = true,
  showLegend = true,
  colors = ['#4A90E2', '#50C878', '#FF6B35', '#7B68EE', '#E74C3C', '#F59E0B', '#10B981', '#6366F1'],
  animate = true,
  centerText,
  onSliceClick
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!svgRef.current || !data.length) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const radius = Math.min(width, height) / 2 - (showLegend ? 80 : 40)
    const innerRad = radius * innerRadius

    const total = d3.sum(data, d => d.value)

    const colorScale = d3.scaleOrdinal<string>()
      .domain(data.map(d => d.label))
      .range(colors)

    const pie = d3.pie<DataPoint>()
      .value(d => d.value)
      .sort(null)
      .padAngle(0.02)

    const arc = d3.arc<d3.PieArcDatum<DataPoint>>()
      .innerRadius(innerRad)
      .outerRadius(radius)
      .cornerRadius(4)

    const hoverArc = d3.arc<d3.PieArcDatum<DataPoint>>()
      .innerRadius(innerRad)
      .outerRadius(radius + 10)
      .cornerRadius(4)

    const labelArc = d3.arc<d3.PieArcDatum<DataPoint>>()
      .innerRadius(radius * 0.85)
      .outerRadius(radius * 0.85)

    const g = svg.append('g')
      .attr('transform', `translate(${showLegend ? width / 2 - 60 : width / 2}, ${height / 2 + (title ? 15 : 0)})`)

    // Slices
    const slices = g.selectAll('.slice')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'slice')

    const paths = slices.append('path')
      .attr('fill', d => d.data.color || colorScale(d.data.label))
      .style('cursor', onSliceClick ? 'pointer' : 'default')

    if (animate) {
      paths
        .attr('d', d => {
          const startAngle = d.startAngle
          return arc({ ...d, startAngle, endAngle: startAngle } as any)
        })
        .transition()
        .duration(1000)
        .attrTween('d', function(d) {
          const interpolate = d3.interpolate({ startAngle: d.startAngle, endAngle: d.startAngle }, d)
          return (t) => arc(interpolate(t) as any) || ''
        })
    } else {
      paths.attr('d', arc as any)
    }

    // Interactions
    const tooltip = d3.select(tooltipRef.current)

    paths
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', hoverArc as any)

        tooltip
          .style('opacity', 1)
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 10}px`)
          .html(`
            <strong>${d.data.label}</strong><br/>
            Value: ${d.data.value.toLocaleString()}<br/>
            Percentage: ${((d.data.value / total) * 100).toFixed(1)}%
          `)
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc as any)

        tooltip.style('opacity', 0)
      })
      .on('click', (_, d) => onSliceClick?.(d.data))

    // Labels
    if (showLabels && radius > 80) {
      const labels = slices.append('text')
        .attr('transform', d => `translate(${labelArc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .style('fill', 'white')
        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.5)')
        .style('opacity', 0)

      if (showPercentages) {
        labels.text(d => {
          const percent = (d.data.value / total) * 100
          return percent > 5 ? `${percent.toFixed(0)}%` : ''
        })
      } else {
        labels.text(d => {
          const percent = (d.data.value / total) * 100
          return percent > 8 ? d.data.label : ''
        })
      }

      labels
        .transition()
        .delay(animate ? 1000 : 0)
        .duration(300)
        .style('opacity', 1)
    }

    // Center text
    if (centerText || innerRadius > 0) {
      const centerGroup = g.append('g')
        .attr('class', 'center-text')

      if (centerText) {
        centerGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '-0.3em')
          .style('font-size', '14px')
          .style('fill', '#6B7280')
          .text(centerText)
      }

      centerGroup.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', centerText ? '1em' : '0.3em')
        .style('font-size', '24px')
        .style('font-weight', '700')
        .style('fill', '#1F2937')
        .text(d3.format('.3s')(total))
    }

    // Title
    if (title) {
      svg.append('text')
        .attr('x', showLegend ? width / 2 - 60 : width / 2)
        .attr('y', 25)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', '600')
        .style('fill', '#1F2937')
        .text(title)
    }

    // Legend
    if (showLegend) {
      const legend = svg.append('g')
        .attr('transform', `translate(${width - 140}, ${height / 2 - (data.length * 12)})`)

      data.forEach((item, i) => {
        const legendItem = legend.append('g')
          .attr('transform', `translate(0, ${i * 24})`)
          .style('cursor', 'pointer')
          .on('click', () => onSliceClick?.(item))

        legendItem.append('rect')
          .attr('width', 16)
          .attr('height', 16)
          .attr('rx', 3)
          .attr('fill', item.color || colorScale(item.label))

        legendItem.append('text')
          .attr('x', 22)
          .attr('y', 12)
          .style('font-size', '12px')
          .style('fill', '#4B5563')
          .text(item.label.length > 15 ? item.label.substring(0, 15) + '...' : item.label)
      })
    }

  }, [data, width, height, innerRadius, title, showLabels, showPercentages, showLegend, colors, animate, centerText, onSliceClick])

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} width={width} height={height} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          opacity: 0,
          backgroundColor: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '13px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
          zIndex: 1000,
          transition: 'opacity 0.2s'
        }}
      />
    </div>
  )
}

export default DonutChart
